import { v4 as uuid } from 'uuid';

import {
    ChatErrorKind,
    ChatEventType,
    ChatPromptActionId,
    ChatPromptInputType,
    ChatRole,
    CHAT_RECEIVE_MESSAGE_TYPE,
    CHAT_RESPONSE_DELTA_TYPE,
    UnsupportedRequestPatterns,
} from './chatConstants';

export const ChatStreamUpdateMode = {
    APPEND: 'append',
    REPLACE: 'replace',
};

export const StreamFinalizationDelayMs = 300;

export const normalizeFailureKind = (failureReason) => {
    if (typeof failureReason === 'object' && failureReason?.kind) {
        return failureReason.kind;
    }

    return failureReason || ChatErrorKind.GENERATION;
};

export const isUnsupportedRequestError = (errorText = '') => {
    const normalizedErrorText = `${errorText}`.trim().toLowerCase();
    if (!normalizedErrorText) return false;

    return UnsupportedRequestPatterns.some((pattern) => normalizedErrorText.includes(pattern));
};

export const buildAssistantMessageFromCompletion = ({ accumulatedText, fallbackMessageContext, payload }) => {
    const completedTimestamp = Date.now();
    const messageId = payload?.message_id || payload?.id || uuid();
    const rawRequestedInteraction = payload?.requested_interaction || payload?.output?.requested_interaction;
    const requestedInteraction = rawRequestedInteraction
        ? {
              ...rawRequestedInteraction,
              id: rawRequestedInteraction.id || `${messageId}:interaction`,
          }
        : null;

    return {
        id: messageId,
        active: true,
        role: ChatRole.ASSISTANT,
        created_on: completedTimestamp,
        modified_on: completedTimestamp,
        message_text: payload?.message_text || accumulatedText,
        message_context: payload?.message_context || fallbackMessageContext,
        help_center_match_ids: payload?.help_center_match_ids,
        ...(requestedInteraction ? { requested_interaction: requestedInteraction } : {}),
    };
};

export const getRequestedInteractionFromAction = ({ payload, requestId }) => {
    const prompt = payload?.requested_interaction || payload?.prompt_data || payload;
    const inputType = prompt.input_type || prompt.action_type || ChatPromptInputType.BINARY;
    const messageId = payload?.message_id || requestId;

    if (prompt.prompt_text || prompt.options || prompt.actions) {
        return {
            ...prompt,
            id: prompt.id || `${messageId}:interaction`,
            input_type: inputType,
            prompt_text: prompt.prompt_text || prompt.title,
            options: prompt.options || prompt.actions || [],
            type: prompt.type || 'confirmation',
        };
    }

    return {
        id: `${messageId}:interaction`,
        input_type: inputType,
        prompt_text: prompt.title,
        type: 'confirmation',
        options: [
            { id: ChatPromptActionId.ACCEPT, label: prompt.primary_label },
            { id: ChatPromptActionId.REJECT, label: prompt.secondary_label },
        ].filter(({ label }) => label),
    };
};

export const getEventConversationId = ({ message = {}, payload = {} }) => {
    return (
        payload.chat_id ||
        payload.conversation_id ||
        payload.conversationId ||
        message.chat_id ||
        message.conversation_id ||
        message.conversationId
    );
};

export const isEventForActiveRequest = ({ activeConversationId, activeRequestId, chatId, message, payload }) => {
    const payloadChatId = getEventConversationId({ message, payload });
    if (chatId && payloadChatId && payloadChatId !== chatId) return false;
    if (activeConversationId && payloadChatId && payloadChatId !== activeConversationId) return false;

    const eventRequestId =
        payload.request_id ||
        payload.requestId ||
        message.request_id ||
        message.requestId ||
        (message.type === CHAT_RECEIVE_MESSAGE_TYPE ? null : payload.id || message.id);

    if (!eventRequestId || !activeRequestId) return true;
    return eventRequestId === activeRequestId;
};

const getReceiveMessageText = (payload = {}) => payload.message_text || payload.chunk?.message_text || '';

export const getStreamTextUpdateFromRuntimeEvent = ({ message, payload, streamedText }) => {
    if (message.type === ChatEventType.LEGACY_DELTA) {
        const text = message.chunk?.message_text || '';
        return text ? { mode: ChatStreamUpdateMode.APPEND, text } : null;
    }

    if (message.type === CHAT_RESPONSE_DELTA_TYPE) {
        const text = message.delta || '';
        return text ? { mode: ChatStreamUpdateMode.APPEND, text } : null;
    }

    if (message.type === CHAT_RECEIVE_MESSAGE_TYPE) {
        const deltaText = payload.delta || payload.message_text_delta;
        if (deltaText) return { mode: ChatStreamUpdateMode.APPEND, text: deltaText };

        const messageText = getReceiveMessageText(payload);
        if (messageText && messageText !== streamedText) {
            return { mode: ChatStreamUpdateMode.REPLACE, text: messageText };
        }
    }

    return null;
};

export const getFinalPayloadFromRuntimeEvent = ({ fallbackMessageContext, message, payload, streamedText }) => {
    if (message.type === ChatEventType.LEGACY_FINAL) {
        return {
            message_context: message.chunk?.message_context || fallbackMessageContext,
            message_id: message.chunk?.message_id,
            message_text: message.chunk?.message_text || streamedText,
        };
    }

    return payload;
};

export const mergeCompletionPayload = ({ pendingActionPayload, payload = {}, streamedText }) => {
    if (!pendingActionPayload) return payload;

    return {
        ...pendingActionPayload,
        ...payload,
        chat_id: payload.chat_id || pendingActionPayload.chat_id,
        message_id: payload.message_id || pendingActionPayload.message_id,
        message_text: payload.message_text || streamedText || pendingActionPayload.message_text,
        requested_interaction: payload.requested_interaction || pendingActionPayload.requested_interaction,
    };
};

export const getOutOfSyncChatReconciliation = ({ chatId, message }) => {
    if (message.type !== 'pong' || !Array.isArray(message.chat_reconciliations) || !chatId) {
        return null;
    }

    return (
        message.chat_reconciliations.find(({ chat_id: reconciledChatId, out_of_sync: outOfSync }) => {
            return reconciledChatId === chatId && outOfSync;
        }) || null
    );
};
